'use client'
import React, {
  ReactNode,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import { Carousel } from '@mantine/carousel'
import '@mantine/carousel/styles.css'
import Autoplay from 'embla-carousel-autoplay'
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'

export interface SimpleSliderHandle {
  scrollPrev: () => void
  scrollNext: () => void
  scrollTo: (index: number) => void
  canScrollPrev: () => boolean
  canScrollNext: () => boolean
  selectedScrollSnap: () => number
  scrollSnapList: () => number[]
}

interface SimpleSliderProps {
  settings?: {
    dots?: boolean
    arrows?: boolean
    autoplay?: boolean
    autoplayInterval?: number
    speed?: number
    emblaOptions?: EmblaOptionsType
    slideSize?:
      | string
      | { base: string; sm?: string; md?: string; lg?: string; xl?: string }
    slideGap?:
      | string
      | number
      | {
          base: string | number
          sm?: string | number
          md?: string | number
          lg?: string | number
          xl?: string | number
        }
  }
  children: ReactNode
  className?: string
  onSlideChange?: (index: number) => void
}

const DEFAULT_SETTINGS = {
  dots: true,
  arrows: true,
  autoplay: false,
  autoplayInterval: 5000,
  speed: 500,
  slideSize: '100%' as const,
  slideGap: 0,
}

const SimpleSlider = forwardRef<SimpleSliderHandle, SimpleSliderProps>(
  ({ settings = {}, children, className, onSlideChange }, ref) => {
    const [isMounted, setIsMounted] = useState(false)
    const mergedSettings = useMemo(
      () => ({
        ...DEFAULT_SETTINGS,
        ...settings,
      }),
      [settings],
    )

    const autoplay = useRef(
      Autoplay({
        delay: mergedSettings.autoplayInterval,
        stopOnInteraction: true,
      }),
    )

    const [embla, setEmbla] = useState<EmblaCarouselType | null>(null)
    const childrenArray = React.Children.toArray(children)

    useEffect(() => {
      setIsMounted(true)
    }, [])

    // Add this useEffect for slide change events
    useEffect(() => {
      if (!embla || !onSlideChange) return

      const onSelect = () => {
        const selectedIndex = embla.selectedScrollSnap()
        // Call the onSlideChange callback if provided
        onSlideChange(selectedIndex)
      }

      embla.on('select', onSelect)
      
      return () => {
        embla.off('select', onSelect)
      }
    }, [embla, onSlideChange])

    const handleMouseEnter = useCallback(() => {
      if (mergedSettings.autoplay) {
        autoplay.current.stop()
      }
    }, [mergedSettings.autoplay])

    const handleMouseLeave = useCallback(() => {
      if (mergedSettings.autoplay) {
        autoplay.current.play()
      }
    }, [mergedSettings.autoplay])

    useImperativeHandle(
      ref,
      () => ({
        scrollPrev: () => embla?.scrollPrev(),
        scrollNext: () => embla?.scrollNext(),
        scrollTo: (i: number) => embla?.scrollTo(i),
        canScrollPrev: () => embla?.canScrollPrev() || false,
        canScrollNext: () => embla?.canScrollNext() || false,
        selectedScrollSnap: () => embla?.selectedScrollSnap() || 0,
        scrollSnapList: () => embla?.scrollSnapList() || [],
      }),
      [embla],
    )

    if (childrenArray.length === 0) {
      return null
    }

    if (!isMounted) {
      return <div className={`h-full ${className || ''}`}>{childrenArray[0]}</div>
    }

    return (
      <div className={`h-full ${className || ''}`}>
        <Carousel
          height='100%'
          withIndicators={mergedSettings.dots}
          withControls={mergedSettings.arrows}
          slideSize={mergedSettings.slideSize}
          slideGap={mergedSettings.slideGap}
          controlSize={30}
          nextControlIcon={
            <svg width={11} height={11} viewBox='0 0 16 16'>
              <path
                d='M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z'
                fill='currentColor'
              />
            </svg>
          }
          previousControlIcon={
            <svg width={11} height={11} viewBox='0 0 16 16'>
              <path
                d='M8 0L0 8L8 16L9.41 14.59L3.83 9H16V7H3.83L9.41 1.41L8 0Z'
                fill='currentColor'
              />
            </svg>
          }
          emblaOptions={{
            loop: false,
            align: 'start',
            dragFree: false,
            ...(mergedSettings.emblaOptions || {}),
          }}
          plugins={mergedSettings.autoplay ? [autoplay.current] : []}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          styles={{
            control: {
              backgroundColor: 'white',
              color: 'black',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: 'none',
            },
          }}
          classNames={{
            control: 'hover:!bg-[#f8f9fa]',
            indicators: '!gap-[6px]',
            indicator: 'data-[active]:!bg-white !w-[6px] !h-[6px] !min-w-[6px] !p-0 !rounded-full !bg-white/75',
          }}
          className='h-full'
          getEmblaApi={setEmbla}
        >
          {childrenArray.map((child, index) => (
            <Carousel.Slide key={index} className='h-full'>
              {child}
            </Carousel.Slide>
          ))}
        </Carousel>
      </div>
    )
  },
)

SimpleSlider.displayName = 'SimpleSlider'
export default SimpleSlider